import { Breadcrumb, Button, Layout, Space, Spin, Table, Tag, Typography } from 'antd';
import dayjs from 'dayjs';
import _ from 'lodash';
import React from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { Dispatch } from 'redux';
import { administratorsListAction } from '~Administrators/actions';
import { administratorsActiveSelector } from '~Administrators/selectors';
import IAdministrator from '~Api/Administrator/IAdministrator';
import IApplication from '~Api/Application/IApplication';
import WorkflowStatusEnum from '~Api/Application/WorkflowStatusEnum';
import LoanProcessorSelector from '~Deals/LoanProcessorSelector';
import { IGlobalState } from '~reducer';
import {
    applicationListSettingsSetAction,
    applicationsPaginatedListAction,
} from './actions';
import './applications.less';
import Search from './Search';
import {
    applicationListSettingsSelector,
    applicationsPaginatedCountSelector,
    applicationsPaginatedSelector,
} from './selectors';
import { IDictionary } from '~utilities/IDictionary';
import { ColumnType } from 'antd/lib/table';
import { ColumnFilterItem, FilterValue, SortOrder, SorterResult, TablePaginationConfig } from 'antd/lib/table/interface';
import AssigneeSelector from './Application/AssigneeSelector';
import ListSortFieldEnum from '~Api/Application/ListSortFieldEnum';
import ListSortOrderEnum from '~Api/Application/ListSortOrderEnum';

export enum WorkflowStatusClosedEnum {
    Closed = 'CLOSED',
}

export type ExtendedWorkflowStatus = WorkflowStatusEnum | WorkflowStatusClosedEnum;

const workflowStatusLabels: IDictionary<string> = {
    [WorkflowStatusEnum.Draft]: 'Draft',
    [WorkflowStatusEnum.New]: 'New',
    [WorkflowStatusEnum.ConditionalApproval]: 'Conditional Approval',
    [WorkflowStatusEnum.Underwriting]: 'Underwriting',
    [WorkflowStatusEnum.FormalApproval]: 'Formal Approval',
    [WorkflowStatusEnum.LegalDocuments]: 'Legal Documents',
    [WorkflowStatusEnum.Settlement]: 'Settlement',
    [WorkflowStatusEnum.Warehoused]: 'Settled',
    [WorkflowStatusClosedEnum.Closed]: 'Closed',
};

const tableSortFieldMappings: IDictionary<ListSortFieldEnum> = {
    createdTime: ListSortFieldEnum.CreatedTime,
    loanAmount: ListSortFieldEnum.LoanAmount,
};

const tableSortOrderMappings: IDictionary<ListSortOrderEnum> = {
    ascend: ListSortOrderEnum.Ascending,
    descend: ListSortOrderEnum.Descending,
};

export interface IApplicationListSettings {
    currentPage: number;
    loanOfficerFilter: string[];
    loanProcessorFilter: string[];
    pageSize: number;
    sortField: ListSortFieldEnum;
    sortOrder: SortOrder;
    statusFilter: ExtendedWorkflowStatus[];
}

interface IPropsSelector {
    activeAdministrators: IAdministrator[];
    applicationListSettings: IApplicationListSettings;
    applications: IDictionary<IApplication>;
    applicationsPaginatedCount: number;
}

interface IPropsDispatch {
    administratorsList: () => void;
    applicationListSettingsSet: (
        currentPage: number,
        loanOfficerFilter: string[],
        loanProcessorFilter: string[],
        pageSize: number,
        sortField: ListSortFieldEnum,
        sortOrder: SortOrder,
        statusFilter: ExtendedWorkflowStatus[],
    ) => void;
    applicationsPaginatedList: (
        currentPage: number,
        loanOfficerFilter: string[],
        loanProcessorFilter: string[],
        pageSize: number,
        sortField: ListSortFieldEnum,
        sortOrder: ListSortOrderEnum,
        statusFilter: ExtendedWorkflowStatus[],
    ) => void;
}

type Props = IPropsSelector & IPropsDispatch;

class List extends React.Component<Props> {
    private refreshInterval: NodeJS.Timeout;

    constructor(props: Props) {
        super(props);

        this.onChangeTable = this.onChangeTable.bind(this);
    }

    public componentDidMount() {
        const { activeAdministrators, applicationListSettings, applications } = this.props;

        if (!applications) {
            this.props.applicationsPaginatedList(
                applicationListSettings.currentPage,
                applicationListSettings.loanOfficerFilter,
                applicationListSettings.loanProcessorFilter,
                applicationListSettings.pageSize,
                ListSortFieldEnum.CreatedTime,
                ListSortOrderEnum.Descending,
                applicationListSettings.statusFilter,
            );
        }

        if (!activeAdministrators) {
            this.props.administratorsList();
        }

    }

    public componentWillUnmount() {
        clearInterval(this.refreshInterval);
    }

    public render(): JSX.Element {
        const { activeAdministrators, applicationListSettings, applications, applicationsPaginatedCount } = this.props;

        if (!applications || !activeAdministrators) {
            return (
                <Layout className='applications'>
                    <Breadcrumb className='breadcrumb'>
                        <Breadcrumb.Item>Home</Breadcrumb.Item>
                        <Breadcrumb.Item>Applications</Breadcrumb.Item>
                    </Breadcrumb>
                    <Layout className='content-wrapper'>
                        <Layout.Content className='content'>
                            <Spin />
                        </Layout.Content>
                    </Layout>
                </Layout>
            );
        }

        const currencyFormatter: Intl.NumberFormat = new Intl.NumberFormat('en-AU', {
            currency: 'AUD',
            style: 'currency',
        });

        const workflowFilters: ColumnFilterItem[] = _.keys(workflowStatusLabels).map((workflowStatus: string) => ({
            text: workflowStatusLabels[workflowStatus],
            value: workflowStatus,
        }));

        const columns: ColumnType<IApplication>[] = [
            {
                key: 'code',
                render: (application: IApplication) => (
                    <Space>
                        <Link to={`/applications/${application.uuid}`}>{application.code}</Link>
                        {application.isExtension && <Tag color='blue'>E</Tag>}
                    </Space>
                ),
                title: 'Code',
                width: '10%',
            },
            {
                dataIndex: 'formattedName',
                key: 'name',
                render: (formattedName: string, application: IApplication) => <Link to={`/applications/${application.uuid}`}>{formattedName}</Link>,
                title: 'Name',
            },
            {
                dataIndex: 'loanAmount',
                key: 'loanAmount',
                render: (loanAmount: number) => currencyFormatter.format(loanAmount),
                sortOrder: applicationListSettings.sortField === ListSortFieldEnum.LoanAmount ? applicationListSettings.sortOrder : null,
                sorter: true,
                title: 'Loan Amount',
                width: '15%',
            },
            {
                filteredValue: applicationListSettings.loanOfficerFilter,
                filters: activeAdministrators.map((administrator: IAdministrator) => ({
                    text: administrator.name,
                    value: administrator.uuid,
                })),
                key: 'loanOfficer',
                render: (application: IApplication) => <AssigneeSelector application={application} />,
                title: 'Loan Officer',
                width: '15%',
            },
            {
                filteredValue: applicationListSettings.loanProcessorFilter,
                filters: activeAdministrators.map((administrator: IAdministrator) => ({
                    text: administrator.name,
                    value: administrator.uuid,
                })),
                key: 'loanProcessor',
                render: (application: IApplication) => <LoanProcessorSelector deal={application.deal} />,
                title: 'Loan Processor',
                width: '15%',
            },
            {
                dataIndex: 'workflowStatus',
                filteredValue: applicationListSettings.statusFilter,
                filters: workflowFilters,
                render: (workflowStatus: WorkflowStatusEnum, application: IApplication) => (workflowStatus === WorkflowStatusEnum.Warehoused || !application.closeReason) ? workflowStatusLabels[workflowStatus] : workflowStatusLabels[WorkflowStatusClosedEnum.Closed],
                title: 'Status',
                width: '15%',
            },
            {
                dataIndex: 'createdTime',
                defaultSortOrder: 'descend',
                key: 'createdTime',
                render: (createdTime: string) => dayjs(createdTime).fromNow(),
                sortOrder: applicationListSettings.sortField === ListSortFieldEnum.CreatedTime ? applicationListSettings.sortOrder : null,
                sorter: true,
                title: 'Created',
                width: '10%',
            },
        ];

        return (
            <Layout className='applications'>
                <Breadcrumb className='breadcrumb'>
                    <Breadcrumb.Item>Home</Breadcrumb.Item>
                    <Breadcrumb.Item>Applications</Breadcrumb.Item>
                </Breadcrumb>
                <Layout className='content-wrapper'>
                    <Layout.Content className='content'>
                        <Space className='actions'>
                            <Search />
                            <Link to='/applications'><Button>Board View</Button></Link>
                        </Space>
                        <Typography.Title level={2}>Applications</Typography.Title>
                        <Table
                            columns={columns}
                            dataSource={_.values(applications)}
                            loading={!applications}
                            onChange={this.onChangeTable}
                            pagination={{ current: applicationListSettings.currentPage, pageSize: applicationListSettings.pageSize, showSizeChanger: true, total: applicationsPaginatedCount }}
                            rowKey='uuid'
                            size='middle'
                        />
                    </Layout.Content>
                </Layout>
            </Layout>
        );
    }

    private onChangeTable(pagination: TablePaginationConfig, filters: Record<string, FilterValue | null>, sorter: SorterResult<IApplication> | SorterResult<IApplication>[]): void {
        const { current, pageSize } = pagination;
        const actualSorter: SorterResult<IApplication> = sorter as SorterResult<IApplication>;
        const loanOfficerFilter: string[] = (filters.loanOfficer as string[]) || [];
        const loanProcessorFilter: string[] = (filters.loanProcessor as string[]) || [];
        const sortField: ListSortFieldEnum = tableSortFieldMappings[actualSorter.columnKey] || ListSortFieldEnum.CreatedTime;
        const workflowStatusFilter: ExtendedWorkflowStatus[] = (filters.workflowStatus as ExtendedWorkflowStatus[]) || [];

        this.props.applicationsPaginatedList(
            current,
            loanOfficerFilter,
            loanProcessorFilter,
            pageSize,
            sortField,
            tableSortOrderMappings[actualSorter.order] || ListSortOrderEnum.Descending,
            workflowStatusFilter,
        );

        this.props.applicationListSettingsSet(
            current,
            loanOfficerFilter,
            loanProcessorFilter,
            pageSize,
            sortField,
            actualSorter.order,
            workflowStatusFilter,
        );
    }
}

function mapStateToProps(state: IGlobalState): IPropsSelector {
    return {
        activeAdministrators: administratorsActiveSelector(state),
        applicationListSettings: applicationListSettingsSelector(state),
        applications: applicationsPaginatedSelector(state),
        applicationsPaginatedCount: applicationsPaginatedCountSelector(state),
    };
}

function mapDispatchToProps(dispatch: Dispatch): IPropsDispatch {
    return {
        administratorsList: () => dispatch(administratorsListAction()),
        applicationListSettingsSet: (
            currentPage: number,
            loanOfficerFilter: string[],
            loanProcessorFilter: string[],
            pageSize: number,
            sortField: ListSortFieldEnum,
            sortOrder: SortOrder,
            statusFilter: ExtendedWorkflowStatus[],
        ) => dispatch(applicationListSettingsSetAction({
            currentPage,
            loanOfficerFilter,
            loanProcessorFilter,
            pageSize,
            sortField,
            sortOrder,
            statusFilter,
        })),
        applicationsPaginatedList: (
            currentPage: number,
            loanOfficerFilter: string[],
            loanProcessorFilter: string[],
            pageSize: number,
            sortField: ListSortFieldEnum,
            sortOrder: ListSortOrderEnum,
            statusFilter: ExtendedWorkflowStatus[],
        ) => dispatch(applicationsPaginatedListAction(
            loanOfficerFilter,
            loanProcessorFilter,
            sortOrder,
            sortField,
            currentPage,
            pageSize,
            statusFilter,
        )),
    };
}

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(List);
